/**
 * Incident fixtures — the port of the DOORS (N5, G-PRC-10, P22), not of any
 * live alerting. The server keeps an `acs_incidents` row per operator-opened
 * incident and answers seven doors: correlate + list + read (`alarm.read`)
 * and create / note / attach / close (`incident.manage` at edit, human-only).
 * This file ports the rules those doors enforce so the dev console and the
 * real façade tell the operator the same story:
 *
 *  - `INCIDENT_STATES` / `INCIDENT_TIMELINE_KINDS` are `models.py`'s own
 *    alphabets — two states, one way; four entry kinds, no free-form one.
 *  - the correlate PROPOSALS are reduced from the fixture's OWN alarm feed
 *    (`deriveAlarmRows` + `associationOf`) by the SAME rule the server runs:
 *    distinct devices, not rows; the floor of two; the worst severity; the
 *    unassociated counted. A second pre-built proposal table would be two
 *    stories (the V3b lesson) — this is one reduction of one feed.
 *  - create derives `severity` and `olt_id` exactly as the door does: the
 *    worst attached alarm, and the OLT only when EVERY attached device
 *    shares one. An incident that spans two OLTs carries `null`.
 *  - the refusal sentences are the server's (`routers/incidents.py`), so a
 *    fixture refusal and a real one read the same on screen.
 *
 * The named divergence (plan D4, ALM-05's own): nothing here raises, delivers
 * or suppresses. The fixture has no live alarm stream; its incident rows are
 * the doors' own data and its proposals are a reduction of the static feed.
 */

import type {
  Alarm,
  AlarmSeverity,
  Incident,
  IncidentCorrelateResponse,
  IncidentGroup,
  IncidentState,
  IncidentTimelineEntry,
} from '../api/types';
import { deriveAlarmRows } from './alarms';
import { FIXTURE_DEVICES } from './devices';
import { isoAgo } from './devices';
import { associationOf } from './olts';

/** `INCIDENT_STATES` in `acs_service/models.py`. */
export const INCIDENT_STATES: readonly IncidentState[] = ['open', 'closed'];

/** `INCIDENT_TIMELINE_KINDS` in `acs_service/models.py`. */
export const INCIDENT_TIMELINE_KINDS = ['created', 'note', 'attach', 'close'] as const;

/**
 * The severity rank the correlate rule and the create derivation share
 * (`acs_service/incidents.py::SEVERITY_RANK`) — a critical alarm outranks a
 * warning on the same OLT.
 */
export const INCIDENT_SEVERITY_RANK: Readonly<Record<AlarmSeverity, number>> = {
  critical: 3,
  warning: 2,
  info: 1,
};

/**
 * The rule's own floor (`MIN_DEVICES_FLOOR`): «چندین» آلارم means more than
 * one DEVICE. A caller asking for one is refused at the door, not honoured.
 */
export const CORRELATE_MIN_DEVICES_FLOOR = 2;

/** The correlate walk's bound (`SCAN_LIMIT`) — the fixture feed is far below
 * it, and the flag exists so a bigger feed degrades honestly. */
export const CORRELATE_SCAN_LIMIT = 2000;

/**
 * The named human the fixture records as `created_by` / `closed_by` — the
 * same stand-in `MAINTENANCE_WINDOW_PLANNER` plays for ALM-05.
 */
export const INCIDENT_OPERATOR = 'user:console-owner';

/** The door's 404 wording, verbatim (`routers/incidents.py::_require_incident`). */
export function unknownIncidentDetail(incidentId: string): string {
  return `no incident with id '${incidentId}' is known`;
}

/** The 409 wording of a second close (`close_incident` / `note_incident`). */
export function incidentClosedDetail(incidentId: string): string {
  return `incident ${incidentId} is already closed; its closure stands and ` + 'is not repeated';
}

/** The create/note clock: the fixture stamps like the server does (ISO Z). */
export function fixtureIncidentClock(now: Date = new Date()): string {
  return now.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/** One war-room entry, spelled once. */
export function incidentTimelineEntry(
  kind: IncidentTimelineEntry['kind'],
  text: string | null,
  at: string,
  actor: string = INCIDENT_OPERATOR,
): IncidentTimelineEntry {
  return { at, kind, text, actor };
}

export interface CorrelateInput {
  /** The OPEN alarm rows of the feed (the caller filters `state !== 'cleared'`). */
  rows: readonly Alarm[];
  /** `acs_device_id → olt_id | null`, from the fixture's association rule. */
  oltOf: ReadonlyMap<string, string | null>;
  minDevices?: number;
  asOf: string;
}

/**
 * The port of `acs_service/incidents.py::correlate`: open alarm rows grouped
 * by the OLT their devices live on, proposed only when the group carries at
 * least `minDevices` DISTINCT devices. Devices with no OLT are COUNTED in
 * `unassociated_devices` — a proposal that hides the population it did not
 * correlate is a lie by omission.
 */
export function correlateFixtureAlarms(input: CorrelateInput): IncidentCorrelateResponse {
  const minDevices = Math.max(
    input.minDevices ?? CORRELATE_MIN_DEVICES_FLOOR,
    CORRELATE_MIN_DEVICES_FLOOR,
  );
  const members = new Map<string, Alarm[]>();
  const devices = new Map<string, Set<string>>();
  const unassociated = new Set<string>();
  let scanned = 0;
  let truncated = false;
  for (const row of input.rows) {
    scanned += 1;
    if (scanned > CORRELATE_SCAN_LIMIT) {
      truncated = true;
      break;
    }
    if (row.state === 'cleared') continue;
    const deviceId = row.acs_device_id;
    if (!deviceId) continue;
    const oltId = input.oltOf.get(deviceId);
    if (!oltId) {
      unassociated.add(deviceId);
      continue;
    }
    const bucket = members.get(oltId) ?? [];
    bucket.push(row);
    members.set(oltId, bucket);
    const seen = devices.get(oltId) ?? new Set<string>();
    seen.add(deviceId);
    devices.set(oltId, seen);
  }
  const groups: IncidentGroup[] = [];
  for (const [oltId, rowsForOlt] of members) {
    const distinct = devices.get(oltId)!;
    if (distinct.size < minDevices) continue;
    let best = 0;
    let label: AlarmSeverity = 'info';
    for (const row of rowsForOlt) {
      const rank = INCIDENT_SEVERITY_RANK[row.severity] ?? 0;
      if (rank > best) {
        best = rank;
        label = row.severity;
      }
    }
    groups.push({
      olt_id: oltId,
      device_count: distinct.size,
      alarm_count: rowsForOlt.length,
      severity: label,
      devices: [...distinct].sort(),
      alarm_ids: rowsForOlt.map((row) => row.alarm_id).sort(),
    });
  }
  groups.sort((a, b) => b.device_count - a.device_count || (a.olt_id < b.olt_id ? -1 : 1));
  return {
    groups,
    unassociated_devices: unassociated.size,
    scanned,
    truncated,
    as_of: input.asOf,
  };
}

/**
 * The severity a created incident carries — the WORST attached alarm, `info`
 * when none (`incidents.worst_severity`). An incident with no alarms is
 * honest about being quiet.
 */
export function worstFixtureSeverity(severities: readonly AlarmSeverity[]): AlarmSeverity {
  let best = -1;
  let label: AlarmSeverity = 'info';
  for (const severity of severities) {
    const rank = INCIDENT_SEVERITY_RANK[severity] ?? 0;
    if (rank > best) {
      best = rank;
      label = severity;
    }
  }
  return label;
}

/** The one OLT every device shares, or `null` — never the first one found. */
export function sharedOltOf(oltIds: readonly (string | null)[]): string | null {
  const set = new Set(oltIds.filter((id): id is string => Boolean(id)));
  return set.size === 1 ? set.values().next().value! : null;
}

/**
 * The two seeds tell both lifecycle stories (the maintenance fixture's rule):
 * one incident still open — born from two alarms, carrying the war-room's
 * first note — and one closed WITH the closer's name and the close entry.
 * `FIXTURE_INCIDENT_ALARM_IDS` names rows of the fixture's OWN feed, so a
 * seed can never cite an alarm the alarms page cannot show.
 */
function seedIncidentSource(): { alarmIds: string[]; oltId: string | null } {
  /** Two alarms the fixture's OWN association puts on ONE OLT — the seed cites
   *  rows the alarms page can actually show, never invented ids, and carries
   *  the OLT that association names (the same derivation the create door runs). */
  const rows = deriveAlarmRows(new Map()).filter((row) => row.state !== 'cleared');
  const oltOf = new Map<string, string | null>();
  FIXTURE_DEVICES.forEach((device, index) => {
    const link = associationOf(device, index);
    oltOf.set(device.acs_device_id, link ? link.olt_id : null);
  });
  const byOlt = new Map<string, string[]>();
  for (const row of rows) {
    const oltId = oltOf.get(row.acs_device_id);
    if (!oltId) continue;
    byOlt.set(oltId, [...(byOlt.get(oltId) ?? []), row.alarm_id]);
  }
  for (const [oltId, ids] of byOlt) {
    if (ids.length >= 2) return { alarmIds: ids.slice(0, 2), oltId };
  }
  return { alarmIds: [], oltId: null };
}

const SEED = seedIncidentSource();
const SEED_ALARM_IDS = SEED.alarmIds;

const SEED_OPEN: Incident = {
  incident_id: 'inc:0f1e2d3c4b5a',
  title: 'الگوی قطعیِ مشترک یک OLT',
  severity: 'critical',
  state: 'open',
  olt_id: SEED.oltId,
  created_at: isoAgo(42),
  created_by: INCIDENT_OPERATOR,
  closed_at: null,
  closed_by: null,
  alarm_ids: [...SEED_ALARM_IDS],
  timeline: [
    incidentTimelineEntry('created', 'گزارش میدان: فیبر اصلی', isoAgo(42)),
    incidentTimelineEntry('note', 'تیم اعزامی در مسیر است', isoAgo(30)),
  ],
};

const SEED_CLOSED: Incident = {
  incident_id: 'inc:9a8b7c6d5e4f',
  title: 'قطعی حل‌شدهٔ روز قبل',
  severity: 'warning',
  state: 'closed',
  olt_id: null,
  created_at: isoAgo(1_500),
  created_by: INCIDENT_OPERATOR,
  closed_at: isoAgo(1_200),
  closed_by: INCIDENT_OPERATOR,
  alarm_ids: [],
  timeline: [
    incidentTimelineEntry('created', null, isoAgo(1_500)),
    incidentTimelineEntry('close', 'فیبر جوش خورد', isoAgo(1_200)),
  ],
};

export const FIXTURE_INCIDENTS: readonly Incident[] = [SEED_OPEN, SEED_CLOSED];
